import React, { useState, useEffect, useRef } from 'react';
import axios from '../api/axios';
import { handleAddItem } from '../utils/crmActions';
import { User, Building2, MapPin, Info, Users } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'lead', label: 'Lead' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'archived', label: 'Archived' },
  { value: 'lost', label: 'Lost' },
  { value: 'prospect', label: 'Prospect' },
];

const PCM_OPTIONS = [
  { value: '', label: '— Select —' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'sms', label: 'SMS' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

/* -------------------------------- UI helpers ------------------------------- */
const INPUT_BASE =
  'block w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-[0.95rem] leading-6 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500';
const SELECT_BASE = INPUT_BASE + ' pr-9';
const LABEL_BASE = 'block text-[13px] font-medium text-gray-700';
const HINT_BASE = 'text-[12px] text-gray-500';

function LabeledInput({
  id,
  name,
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  required = false,
  autoComplete,
  inputRef,          // ← added
  invalid = false,   // ← added
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id || name} className={LABEL_BASE}>
        {label} {required ? <span className="text-red-500">*</span> : null}
      </label>
      <input
        ref={inputRef}
        id={id || name}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        type={type}
        autoComplete={autoComplete}
        className={`${INPUT_BASE} ${invalid ? 'border-red-500 focus:border-red-500 focus:ring-red-100' : ''}`}
        required={required}
        aria-invalid={invalid || undefined}
      />
    </div>
  );
}

function LabeledSelect({
  id,
  name,
  label,
  value,
  onChange,
  children,
  required = false,
  disabled = false,
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id || name} className={LABEL_BASE}>
        {label} {required ? <span className="text-red-500">*</span> : null}
      </label>
      <select
        id={id || name}
        name={name}
        value={value}
        onChange={onChange}
        className={SELECT_BASE}
        disabled={disabled}
        required={required}
      >
        {children}
      </select>
    </div>
  );
}

function Section({ icon: Icon, title, hint, children }) {
  return (
    <div className="border rounded-xl p-5 space-y-4 shadow-sm bg-white">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 text-gray-700">
          <Icon size={18} />
        </div>
        <div>
          <div className="text-gray-800 font-semibold">{title}</div>
          {hint ? <div className={HINT_BASE}>{hint}</div> : null}
        </div>
      </div>
      {children}
    </div>
  );
}

/* ------------------------------ utils ------------------------------------- */
const toArray = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res)) return res;
  return [];
};

/* ------------------------------ component --------------------------------- */
const AddClientModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({
    name: '',
    contactPerson: '',
    email: '',
    secondaryEmail: '',
    phone: '',
    secondaryPhone: '',
    company: '',
    industry: '',
    website: '',
    status: 'lead',
    source: '',
    preferredContactMethod: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    address_country: '',
    address_raw: '',
    assignedTo: '',
  });

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [usersError, setUsersError] = useState('');
  const [tagList, setTagList] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [contactError, setContactError] = useState(''); // inline error

  // refs to trigger native validity bubble on failure
  const emailRef = useRef(null);
  const phoneRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const ctrl = new AbortController();
    setLoadingUsers(true);
    setUsersError('');

    // active, non-invited; no role filter (viewer included)
    axios
      .get('/users', { signal: ctrl.signal })
      .then((res) => {
        if (!mounted) return;
        setUsers(toArray(res));
      })
      .catch((err) => {
        if (!mounted || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setUsers([]);
        setUsersError(
          'Failed to load team: ' + (err?.response?.data?.msg || err?.message || 'Unknown error')
        );
      })
      .finally(() => mounted && setLoadingUsers(false));

    return () => {
      mounted = false;
      ctrl.abort();
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    // clear error + native validity on any contact change
    if (['email', 'secondaryEmail', 'phone', 'secondaryPhone'].includes(name)) {
      setContactError('');
      if (emailRef.current) emailRef.current.setCustomValidity('');
      if (phoneRef.current) phoneRef.current.setCustomValidity('');
    }
  };

  const addTag = () => {
    const t = newTag.trim();
    if (t && !tagList.includes(t)) {
      setTagList((prev) => [...prev, t]);
      setNewTag('');
    }
  };

  const removeTag = (tag) => setTagList((prev) => prev.filter((t) => t !== tag));

  const handleSubmit = async (e) => {
    e.preventDefault();

    // one-of validation: require at least email OR phone (consider secondary as well)
    const hasEmail = (form.email || '').trim() || (form.secondaryEmail || '').trim();
    const hasPhone = (form.phone || '').trim() || (form.secondaryPhone || '').trim();

    if (!hasEmail && !hasPhone) {
      const msg = 'Please provide at least one contact: an email or a phone number.';
      setContactError(msg);

      // show native validation bubble on the first contact field we have
      if (emailRef.current) {
        emailRef.current.setCustomValidity(msg);
        emailRef.current.reportValidity();
      } else if (phoneRef.current) {
        phoneRef.current.setCustomValidity(msg);
        phoneRef.current.reportValidity();
      }
      return;
    }

    const address = {
      street: form.address_street,
      city: form.address_city,
      state: form.address_state,
      zip: form.address_zip,
      country: form.address_country,
      raw: form.address_raw,
    };

    const payload = {
      ...form,
      address,
      assignedTo: form.assignedTo || undefined,
      tags: tagList,
    };

    // strip flattened address_* keys
    Object.keys(payload).forEach((k) => {
      if (k.startsWith('address_')) delete payload[k];
    });

    await handleAddItem('clients', payload, onSuccess, onClose);
  };

  const formRef = useRef();
  const team = Array.isArray(users) ? users : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="relative bg-white w-full max-w-6xl rounded-2xl shadow-2xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-t-2xl">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <Building2 size={32} />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-semibold truncate">Add New Client</h2>
              <p className="text-sm text-blue-100">
                Fill out the details to create a new client record.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="bg-white/20 hover:bg-white/30 rounded-full p-2"
            type="button"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form
          id="add-client-form"
          ref={formRef}
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-8 space-y-8 bg-gray-50/50"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Fields */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Info */}
              <Section
                icon={Building2}
                title="Basic Information"
                hint="Core identification details of the client"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <LabeledInput
                    name="name"
                    label="Full Name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="e.g., Acme Headquarters or John Doe"
                    required
                    autoComplete="name"
                  />
                  <LabeledInput
                    name="company"
                    label="Company"
                    value={form.company}
                    onChange={handleChange}
                    placeholder="e.g., Asteriq"
                    autoComplete="organization"
                  />
                  <LabeledInput
                    name="industry"
                    label="Industry"
                    value={form.industry}
                    onChange={handleChange}
                    placeholder="e.g., Technology"
                  />
                  <LabeledInput
                    name="website"
                    label="Website"
                    value={form.website}
                    onChange={handleChange}
                    placeholder="https://example.com"
                    autoComplete="url"
                  />
                </div>
              </Section>

              {/* Contact Info */}
              <Section icon={User} title="Contact Information" hint="How to reach this client">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <LabeledInput
                    name="contactPerson"
                    label="Contact Person"
                    value={form.contactPerson}
                    onChange={handleChange}
                    placeholder="e.g., Jane Smith"
                    autoComplete="name"
                  />
                  <LabeledInput
                    name="email"
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="name@company.com"
                    autoComplete="email"
                    inputRef={emailRef}
                    invalid={!!contactError}
                  />
                  <LabeledInput
                    name="secondaryEmail"
                    label="Secondary Email"
                    type="email"
                    value={form.secondaryEmail}
                    onChange={handleChange}
                    placeholder="optional"
                    autoComplete="email"
                  />
                  <LabeledInput
                    name="phone"
                    label="Phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    autoComplete="tel"
                    inputRef={phoneRef}
                    invalid={!!contactError}
                  />
                  <LabeledInput
                    name="secondaryPhone"
                    label="Secondary Phone"
                    value={form.secondaryPhone}
                    onChange={handleChange}
                    placeholder="optional"
                    autoComplete="tel"
                  />
                </div>

                {contactError ? (
                  <div className="mt-2 text-sm text-red-600" aria-live="polite">{contactError}</div>
                ) : (
                  <div className="mt-2 text-sm text-gray-500">
                    Provide <span className="font-medium">at least one</span> contact: email or phone.
                  </div>
                )}
              </Section>

              {/* Address */}
              <Section icon={MapPin} title="Address" hint="Location details for billing and communication">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <LabeledInput
                    name="address_street"
                    label="Street"
                    value={form.address_street}
                    onChange={handleChange}
                    placeholder="1200 York Mills Road"
                    autoComplete="address-line1"
                  />
                  <LabeledInput
                    name="address_city"
                    label="City"
                    value={form.address_city}
                    onChange={handleChange}
                    placeholder="New York"
                    autoComplete="address-level2"
                  />
                  <LabeledInput
                    name="address_state"
                    label="State/Province"
                    value={form.address_state}
                    onChange={handleChange}
                    placeholder="NY"
                    autoComplete="address-level1"
                  />
                  <LabeledInput
                    name="address_zip"
                    label="ZIP/Postal Code"
                    value={form.address_zip}
                    onChange={handleChange}
                    placeholder="10001"
                    autoComplete="postal-code"
                  />
                  <LabeledInput
                    name="address_country"
                    label="Country"
                    value={form.address_country}
                    onChange={handleChange}
                    placeholder="United States"
                    autoComplete="country-name"
                  />
                  <LabeledInput
                    name="address_raw"
                    label="Full Address (raw)"
                    value={form.address_raw}
                    onChange={handleChange}
                    placeholder="Paste a full address string"
                    autoComplete="off"
                  />
                </div>
              </Section>

              {/* Additional */}
              <Section icon={Info} title="Additional Information" hint="Other useful notes and preferences">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <LabeledInput
                    name="source"
                    label="Source"
                    value={form.source}
                    onChange={handleChange}
                    placeholder="e.g., Referral, Google Search"
                  />

                  {/* Preferred Contact Method → Dropdown */}
                  <LabeledSelect
                    name="preferredContactMethod"
                    label="Preferred Contact Method"
                    value={form.preferredContactMethod}
                    onChange={handleChange}
                  >
                    {PCM_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </LabeledSelect>
                </div>

                {/* Tags */}
                <div className="mt-2">
                  <div className={LABEL_BASE}>Tags</div>
                  <div className={HINT_BASE}>Use Enter to add a tag</div>
                  <div className="flex flex-wrap gap-2 mt-2 mb-2">
                    {tagList.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium border border-blue-100"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="rounded-full px-1 text-blue-600 hover:bg-blue-100"
                          aria-label={`Remove ${tag}`}
                          title="Remove"
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addTag();
                        }
                      }}
                      placeholder="Add a tag and press Enter"
                      className={INPUT_BASE + ' flex-1'}
                    />
                    <button
                      type="button"
                      onClick={addTag}
                      className="px-3.5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 shadow-sm"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </Section>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Section icon={Users} title="Status & Assignment" hint="Control pipeline state and owner">
                <div className="space-y-4">
                  <LabeledSelect name="status" label="Client Status" value={form.status} onChange={handleChange}>
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </LabeledSelect>

                  <LabeledSelect
                    name="assignedTo"
                    label="Assigned To"
                    value={form.assignedTo}
                    onChange={handleChange}
                    disabled={loadingUsers}
                  >
                    <option value="">— Unassigned —</option>
                    {loadingUsers && <option disabled>Loading team…</option>}
                    {team.map((u) => (
                      <option key={u.id || u._id} value={u.id || u._id}>
                        {u.name} {u.role ? `(${u.role})` : ''}
                      </option>
                    ))}
                  </LabeledSelect>
                  {usersError && (
                    <div className="text-xs text-red-500" aria-live="polite">
                      {usersError}
                    </div>
                  )}
                </div>
              </Section>
            </div>
          </div>
        </form>

        {/* Sticky Footer */}
        <div className="px-8 py-4 bg-white border-t flex justify-end gap-3 shadow-lg rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 rounded-lg text-gray-700 font-medium hover:bg-gray-100 border border-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-client-form"
            className="px-7 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 shadow-sm"
          >
            Add Client
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddClientModal;
